<template>
  <div class="alert-settings-container">
    <el-card>
      <div slot="header" class="header-section">
        <h2>告警设置</h2>
        <div class="header-actions">
          <el-select 
            v-model="selectedBridge" 
            placeholder="请选择桥梁"
            filterable
            @change="handleBridgeChange">
            <el-option
              v-for="item in bridges"
              :key="item.id"
              :label="item.bridgeName"
              :value="item.id">
            </el-option>
          </el-select>
        </div>
      </div>
      
      <div v-if="!selectedBridge" class="empty-tip">
        <i class="el-icon-warning-outline"></i>
        <span>请先选择桥梁，再进行告警规则配置</span>
      </div>

      <div v-else>
        <div class="toolbar">
          <el-button type="primary" icon="el-icon-plus" @click="handleAddRule">
            添加告警规则
          </el-button>
          <el-button type="success" icon="el-icon-document-copy" @click="handleOpenTemplates">
            从模板创建
          </el-button>
        </div>

        <el-table
          :data="alertRules"
          border
          style="width: 100%"
          v-loading="loading">
          <el-table-column prop="ruleName" label="规则名称" min-width="150"></el-table-column>
          <el-table-column prop="areaName" label="区域名称" min-width="150"></el-table-column>
          <el-table-column prop="alertType" label="告警类型" width="120"></el-table-column>
          <el-table-column prop="alertLevel" label="告警级别" width="120"></el-table-column>
          <el-table-column label="告警方式" width="150">
            <template slot-scope="scope">
              <div class="alert-methods">
                <el-tag
                  v-for="method in formatAlertMethods(scope.row.alertMethods)"
                  :key="method.value"
                  size="small"
                  effect="plain"
                  class="alert-method-tag">
                  {{ method.label }}
                </el-tag>
                <span v-if="!scope.row.alertMethods">-</span>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="状态" width="100" align="center">
            <template slot-scope="scope">
              <el-switch
                v-model="scope.row.status"
                :active-value="1"
                :inactive-value="0"
                @change="handleStatusChange(scope.row)">
              </el-switch>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="260" align="center">
            <template slot-scope="scope">
              <el-button size="mini" @click="handleEdit(scope.row)">编辑</el-button>
              <el-button size="mini" type="success" @click="handleCopy(scope.row)">复制</el-button>
              <el-button size="mini" type="danger" @click="handleDelete(scope.row)">删除</el-button>
            </template>
          </el-table-column>
        </el-table>

        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="pagination.pageNum"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="pagination.pageSize"
          layout="total, sizes, prev, pager, next, jumper"
          :total="pagination.total"
          class="pagination">
        </el-pagination>
      </div>
    </el-card>

    <!-- 告警规则表单对话框 -->
    <el-dialog 
      :title="dialogTitle" 
      :visible.sync="dialogVisible" 
      width="650px" 
      @closed="resetForm">
      <el-form 
        :model="ruleForm" 
        :rules="rules" 
        ref="ruleForm" 
        label-width="100px"
        label-position="left">
        <el-form-item label="规则名称" prop="ruleName">
          <el-input v-model="ruleForm.ruleName" placeholder="请输入规则名称"></el-input>
        </el-form-item>
        <el-form-item label="区域" prop="areaId">
          <el-select v-model="ruleForm.areaId" placeholder="请选择区域" @change="handleAreaChange">
            <el-option
              v-for="item in formAreaOptions"
              :key="item.id"
              :label="item.areaName"
              :value="item.id">
            </el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="告警动作" prop="detectionMethod">
          <el-select v-model="ruleForm.detectionMethod" placeholder="请选择告警动作" @change="handleDetectionMethodChange">
            <el-option
              v-for="item in filteredAlertTypes"
              :key="item.value"
              :label="item.label"
              :value="item.value">
            </el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="告警级别" prop="alertLevel">
          <el-select v-model="ruleForm.alertLevel" placeholder="请选择告警级别">
            <el-option
              v-for="item in alertLevels"
              :key="item.value"
              :label="item.label"
              :value="item.value">
            </el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="告警方式" prop="alertMethods">
          <el-select 
            v-model="ruleForm.alertMethods" 
            multiple 
            collapse-tags
            placeholder="请选择告警方式">
            <el-option
              v-for="item in alertMethods"
              :key="item.value"
              :label="item.label"
              :value="item.value">
            </el-option>
          </el-select>
          <div class="form-help">可多选，如需同时发送短信和微信通知等</div>
        </el-form-item>
        <el-form-item label="告警信息" prop="alertMessage">
          <el-input
            type="textarea"
            :rows="4"
            placeholder="请输入告警信息模板，支持变量：{桥梁名}、{船舶名}、{告警动作}、{区域名}、{mmsi}"
            v-model="ruleForm.alertMessage">
          </el-input>
          <div class="form-help">
            <p>支持的变量：</p>
            <el-tag size="small" @click="insertVariable('{桥梁名}')" class="variable-tag">{桥梁名}</el-tag>
            <el-tag size="small" @click="insertVariable('{船舶名}')" class="variable-tag">{船舶名}</el-tag>
            <el-tag size="small" @click="insertVariable('{告警动作}')" class="variable-tag">{告警动作}</el-tag>
            <el-tag size="small" @click="insertVariable('{区域名}')" class="variable-tag">{区域名}</el-tag>
            <el-tag size="small" @click="insertVariable('{mmsi}')" class="variable-tag">{mmsi}</el-tag>
          </div>
        </el-form-item>
        <!-- 规则条件（按告警动作动态显示） -->
        <el-form-item label="规则条件">
          <template v-if="ruleForm.detectionMethod === 'SPEED'">
            <div class="condition-row">
              <span class="condition-label">限速阈值（节）：</span>
              <el-input-number v-model="conditionForm.speedThreshold" :min="1" :max="100" :step="1" controls-position="right"></el-input-number>
              <span class="condition-help">船舶速度超过该值即触发预警</span>
            </div>
          </template>
          <template v-else-if="ruleForm.detectionMethod === 'HIGH'">
            <div class="condition-row">
              <span class="condition-label">净空高度阈值（米）：</span>
              <el-input-number v-model="conditionForm.heightThreshold" :min="1" :max="200" :step="1" controls-position="right"></el-input-number>
              <span class="condition-help">船舶高度超过该值即触发超高预警</span>
            </div>
          </template>
          <template v-else-if="ruleForm.detectionMethod === 'STAY'">
            <div class="condition-row">
              <span class="condition-label">滞留时长阈值（分钟）：</span>
              <el-input-number v-model="conditionForm.durationMinutes" :min="1" :max="600" :step="1" controls-position="right"></el-input-number>
              <span class="condition-help">船舶在区域内停留超过该时长即触发预警</span>
            </div>
          </template>
          <template v-else>
            <el-input
              type="textarea"
              :rows="3"
              placeholder="请输入规则条件（JSON格式），如：{&quot;speedThreshold&quot;: 8}。留空使用默认条件"
              v-model="conditionForm.rawJson">
            </el-input>
          </template>
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-switch
            v-model="ruleForm.status"
            :active-value="1"
            :inactive-value="0"
            active-text="启用"
            inactive-text="禁用">
          </el-switch>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="submitForm">确 定</el-button>
      </div>
    </el-dialog>

    <!-- 模板选择对话框 -->
    <el-dialog
      title="选择告警规则模板"
      :visible.sync="templateDialogVisible"
      width="600px">
      <div v-loading="templateLoading">
        <div v-if="ruleTemplates.length === 0 && !templateLoading" class="empty-tip">
          <span>暂无可用模板</span>
        </div>
        <el-table
          v-else
          :data="ruleTemplates"
          border
          style="width: 100%"
          @row-click="handleSelectTemplate">
          <el-table-column prop="ruleName" label="模板名称" min-width="150"></el-table-column>
          <el-table-column prop="detectionMethod" label="告警动作" width="120"></el-table-column>
          <el-table-column prop="alertLevel" label="告警级别" width="100"></el-table-column>
          <el-table-column label="操作" width="80" align="center">
            <template slot-scope="scope">
              <el-button size="mini" type="primary" @click.stop="handleSelectTemplate(scope.row)">应用</el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="templateDialogVisible = false">关 闭</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import * as alertApi from '@/api/alert'
import * as bridgeApi from '@/api/bridge'
import { getAreasByBridgeId } from '@/api/area'

export default {
  name: 'AlertSettings',
  data() {
    return {
      loading: false,
      bridges: [],
      selectedBridge: null,
      areas: [],
      alertRules: [],
      alertLevels: [],
      alertTypes: [],
      alertMethods: [],
      pagination: {
        pageNum: 1,
        pageSize: 10,
        total: 0
      },
      dialogVisible: false,
      dialogTitle: '添加告警规则',
      ruleForm: {
        ruleName: '',
        areaId: '',
        detectionMethod: '',
        alertLevel: '',
        alertMessage: '',
        alertMethods: [],
        status: 1,
        bridgeId: null
      },
      rules: {
        ruleName: [
          { required: true, message: '请输入规则名称', trigger: 'blur' },
          { min: 2, max: 100, message: '长度在 2 到 100 个字符', trigger: 'blur' }
        ],
        areaId: [
          { required: true, message: '请选择区域', trigger: 'change' }
        ],
        detectionMethod: [
          { required: true, message: '请选择告警动作', trigger: 'change' }
        ],
        alertLevel: [
          { required: true, message: '请选择告警级别', trigger: 'change' }
        ],
        alertMethods: [
          { required: true, message: '请选择至少一种告警方式', trigger: 'change' }
        ],
        alertMessage: [
          { required: true, message: '请输入告警信息', trigger: 'blur' },
          { min: 5, max: 500, message: '长度在 5 到 500 个字符', trigger: 'blur' }
        ]
      },
      currentAreaInfo: null,
      editingRuleId: null,
      // 规则条件表单（按告警动作动态显示）
      conditionForm: {
        speedThreshold: 8,
        heightThreshold: 15,
        durationMinutes: 30,
        rawJson: ''
      },
      // 模板相关
      templateDialogVisible: false,
      templateLoading: false,
      ruleTemplates: []
    }
  },
  created() {
    this.fetchBridges()
    this.fetchAlertLevelOptions()
    this.fetchAlertTypeOptions()
    this.fetchAlertMethodOptions()
  },
  methods: {
    // 加载桥梁列表
    async fetchBridges() {
      try {
        this.loading = true
        const res = await bridgeApi.getAllBridges()
        if (res.code === 200) {
          // 倒序排列桥梁列表
          this.bridges = (res.data || []).reverse()
        } else {
          this.$message.error(res.msg || '获取桥梁列表失败')
        }
      } catch (error) {
        console.error('获取桥梁列表出错：', error)
        this.$message.error('获取桥梁列表失败')
      } finally {
        this.loading = false
      }
    },
    
    // 加载区域列表
    async fetchAreas() {
      if (!this.selectedBridge) return
      
      try {
        this.loading = true
        const res = await getAreasByBridgeId(this.selectedBridge)
        if (res.code === 200) {
          this.areas = res.data || []
          
          // 如果是编辑状态，需要在区域加载后触发一次区域变更处理
          if (this.editingRuleId && this.ruleForm.areaId) {
            this.$nextTick(() => {
              // 确保区域数据已经渲染到DOM
              this.handleAreaChange();
            });
          }
        } else {
          this.$message.error(res.msg || '获取区域列表失败')
        }
      } catch (error) {
        console.error('获取区域列表出错：', error)
        this.$message.error('获取区域列表失败')
      } finally {
        this.loading = false
      }
    },
    
    // 获取告警级别选项
    async fetchAlertLevelOptions() {
      try {
        const res = await alertApi.getAlertLevelOptions()
        if (res.code === 200) {
          this.alertLevels = (res.data || []).map(item => {
            return { label: item.label, value: item.value }
          })
        }
      } catch (error) {
        console.error('获取告警级别选项出错：', error)
      }
    },
    
    // 获取告警方式选项
    async fetchAlertMethodOptions() {
      try {
        const res = await alertApi.getAlertMethodOptions()
        if (res.code === 200) {
          this.alertMethods = (res.data || []).map(item => {
            return { label: item.label, value: item.value }
          })
        }
      } catch (error) {
        console.error('获取告警方式选项出错：', error)
      }
    },
    
    // 获取告警类型选项
    async fetchAlertTypeOptions() {
      try {
        const res = await alertApi.getRuleTypeOptions()
        if (res.code === 200) {
          this.alertTypes = (res.data || []).map(item => {
            return { label: item.label, value: item.value }
          })
        }
      } catch (error) {
        console.error('获取告警类型选项出错：', error)
      }
    },
    
    // 格式化告警方式，将逗号分隔的字符串转换为数组
    formatAlertMethods(alertMethodsStr) {
      if (!alertMethodsStr) return []
      
      const methodValues = alertMethodsStr.split(',')
      return methodValues.map(value => {
        const method = this.alertMethods.find(m => m.value === value)
        return method || { value, label: value }
      })
    },
    
    // 获取告警规则列表
    async fetchAlertRules() {
      if (!this.selectedBridge) return
      
      try {
        this.loading = true
        const params = {
          bridgeId: this.selectedBridge,
          pageNum: this.pagination.pageNum,
          pageSize: this.pagination.pageSize
        }
        
        const res = await alertApi.getAlertRules(params)
        if (res.code === 200) {
          this.alertRules = res.data.list || []
          this.pagination.total = res.data.total || 0
          
          // 添加区域名称
          if (this.alertRules.length > 0 && this.areas.length > 0) {
            this.alertRules.forEach(rule => {
              const area = this.areas.find(a => a.id === rule.areaId)
              rule.areaName = area ? area.areaName : '未知区域'
            })
          }
        } else {
          this.$message.error(res.msg || '获取告警规则列表失败')
        }
      } catch (error) {
        console.error('获取告警规则列表出错：', error)
        this.$message.error('获取告警规则列表失败')
      } finally {
        this.loading = false
      }
    },
    
    // 提交表单
    submitForm() {
      this.$refs.ruleForm.validate(async valid => {
        if (!valid) return

        // 会遇规则只能绑定桥区水域区域
        if (this.ruleForm.detectionMethod === 'MEETING') {
          const area = this.areas.find(a => a.id === this.ruleForm.areaId)
          if (!this.isBridgeWaterArea(area)) {
            this.$message.error('会遇规则只能绑定桥区水域（桥梁区）')
            return
          }
        }
        
        try {
          this.loading = true
          let res
          
          // 构建规则条件 JSON（按告警动作）
          let ruleCondition = {}
          const det = this.ruleForm.detectionMethod
          if (det === 'SPEED') {
            ruleCondition.speedThreshold = this.conditionForm.speedThreshold
          } else if (det === 'HIGH') {
            ruleCondition.heightThreshold = this.conditionForm.heightThreshold
          } else if (det === 'STAY') {
            ruleCondition.durationMinutes = this.conditionForm.durationMinutes
          } else if (this.conditionForm.rawJson && this.conditionForm.rawJson.trim()) {
            try {
              ruleCondition = JSON.parse(this.conditionForm.rawJson.trim())
            } catch (e) {
              this.$message.error('规则条件不是合法的 JSON 格式')
              this.loading = false
              return
            }
          }
          
          // 构建请求数据
          const data = {
            ...this.ruleForm,
            // 将告警方式数组转为逗号分隔的字符串
            alertMethods: Array.isArray(this.ruleForm.alertMethods) 
              ? this.ruleForm.alertMethods.join(',') 
              : this.ruleForm.alertMethods,
            alertAction: JSON.stringify({
              // 根据需求可以添加其他配置
            }),
            ruleCondition: JSON.stringify(ruleCondition),
            ruleType: 'AREA',
            priority: 1
          }
          
          if (this.editingRuleId) {
            // 更新
            res = await alertApi.updateAlertRule(this.editingRuleId, data)
          } else {
            // 创建
            res = await alertApi.createAlertRule(data)
          }
          
          if (res.code === 200) {
            this.$message.success(this.editingRuleId ? '更新成功' : '添加成功')
            this.dialogVisible = false
            this.fetchAlertRules()
          } else {
            this.$message.error(res.msg || (this.editingRuleId ? '更新失败' : '添加失败'))
          }
        } catch (error) {
          console.error(this.editingRuleId ? '更新告警规则出错：' : '添加告警规则出错：', error)
          this.$message.error(this.editingRuleId ? '更新失败' : '添加失败')
        } finally {
          this.loading = false
        }
      })
    },
    
    // 处理桥梁变更
    async handleBridgeChange() {
      await this.fetchAreas()
      await this.fetchAlertRules()
    },
    
    // 处理区域变更
    handleAreaChange() {
      // 获取当前选中的区域信息
      this.currentAreaInfo = this.areas.find(area => area.id === this.ruleForm.areaId) || null
      
      // 清除原有的告警动作，因为不同区域类型支持的动作不同
      this.ruleForm.detectionMethod = '';
      
      // 自动填充一些默认值
      if (this.currentAreaInfo) {
        // 判断是否为线段类型（通过名称包含"线"判断）
        const isLineSegment = this.currentAreaInfo.areaName.includes('线');
        const isNavigationArea = this.currentAreaInfo.areaName.includes('航区');
        const isBridgePier = this.currentAreaInfo.areaName.includes('桥墩');
        
        // 根据区域类型设置默认的告警信息和检测方法
        if (isLineSegment) {
          if (this.currentAreaInfo.areaName.includes('桥梁')) {
            // 桥梁线默认为超高
            this.ruleForm.detectionMethod = 'HIGH';
            this.ruleForm.alertMessage = `{船舶名}在${this.currentAreaInfo.areaName}检测到超高，MMSI：{mmsi}，请关注！`;
          } else {
            // 普通线默认为穿越
            this.ruleForm.detectionMethod = 'CROSS';
            this.ruleForm.alertMessage = `{船舶名}穿越${this.currentAreaInfo.areaName}，MMSI：{mmsi}，请关注！`;
          }
        } else if (isNavigationArea) {
          // 通航区默认为偏航
          this.ruleForm.detectionMethod = 'YAW';
          this.ruleForm.alertMessage = `{船舶名}在${this.currentAreaInfo.areaName}发生偏航，MMSI：{mmsi}，请关注！`;
        } else if (isBridgePier) {
          // 桥墩区域默认为碰撞
          this.ruleForm.detectionMethod = 'COLLISION';
          this.ruleForm.alertMessage = `{桥梁名}：警告！{船舶名}可能与${this.currentAreaInfo.areaName}发生碰撞，MMSI：{mmsi}，请紧急处理！`;
        } else if (this.currentAreaInfo.areaName.includes('桥区')) {
          // 桥区水域默认为会遇
          this.ruleForm.detectionMethod = 'MEETING';
          this.ruleForm.alertMessage = `船舶：{船舶名1}，{船舶名2}，在{桥梁名} {区域名}发生会遇，相距约{距离}米，请注意通航安全`;
        } else {
          // 普通区域默认为进入
          this.ruleForm.detectionMethod = 'ENTRY';
          this.ruleForm.alertMessage = `{船舶名}{告警动作}${this.currentAreaInfo.areaName}，MMSI：{mmsi}，请关注！`;
        }
      }
      
      // 确保只有在有可用选项时才设置默认值
      this.$nextTick(() => {
        if (this.filteredAlertTypes.length > 0 && !this.ruleForm.detectionMethod) {
          this.ruleForm.detectionMethod = this.filteredAlertTypes[0].value;
        }
      });
    },

    // 告警动作变更：会遇规则只能绑定桥区水域
    handleDetectionMethodChange(val) {
      if (val !== 'MEETING') return
      const current = this.areas.find(a => a.id === this.ruleForm.areaId)
      if (current && this.isBridgeWaterArea(current)) {
        return
      }
      const bridgeWater = this.areas.find(a => this.isBridgeWaterArea(a))
      if (bridgeWater) {
        this.ruleForm.areaId = bridgeWater.id
        this.currentAreaInfo = bridgeWater
        this.$message.info(`会遇规则已自动绑定桥区水域：${bridgeWater.areaName}`)
      } else {
        this.ruleForm.detectionMethod = ''
        this.$message.warning('当前桥梁没有可用的桥区水域区域，无法配置会遇规则')
      }
    },

    // 桥区水域：区域类型为 BRIDGE（桥梁区），或名称包含“桥区”
    isBridgeWaterArea(area) {
      if (!area) return false
      return area.areaType === 'BRIDGE' || String(area.areaName || '').includes('桥区')
    },
    
    // 添加规则
    handleAddRule() {
      this.dialogTitle = '添加告警规则'
      this.editingRuleId = null
      this.resetForm()
      this.ruleForm.bridgeId = this.selectedBridge
      this.ruleForm.status = 1
      this.dialogVisible = true
    },
    
    // 编辑规则
    async handleEdit(row) {
      this.dialogTitle = '编辑告警规则'
      this.editingRuleId = row.id
      
      try {
        this.loading = true
        const res = await alertApi.getAlertRuleById(row.id)
        if (res.code === 200) {
          const data = { ...res.data }
          
          // 将告警方式字符串转换为数组
          if (data.alertMethods) {
            data.alertMethods = data.alertMethods.split(',')
          } else {
            data.alertMethods = []
          }
          
          // 解析规则条件 JSON 到条件表单
          this.parseRuleCondition(data.ruleCondition)
          
          this.ruleForm = data
          this.dialogVisible = true
        } else {
          this.$message.error(res.msg || '获取规则详情失败')
        }
      } catch (error) {
        console.error('获取规则详情出错：', error)
        this.$message.error('获取规则详情失败')
      } finally {
        this.loading = false
      }
    },
    
    // 复制规则
    async handleCopy(row) {
      try {
        this.loading = true
        const res = await alertApi.copyAlertRule(row.id)
        if (res.code === 200) {
          this.$message.success('复制成功，新规则已创建')
          this.fetchAlertRules()
        } else {
          this.$message.error(res.msg || '复制失败')
        }
      } catch (error) {
        console.error('复制告警规则出错：', error)
        this.$message.error('复制失败')
      } finally {
        this.loading = false
      }
    },

    // 打开模板选择对话框
    async handleOpenTemplates() {
      if (!this.selectedBridge) {
        this.$message.warning('请先选择桥梁')
        return
      }
      this.templateDialogVisible = true
      this.templateLoading = true
      try {
        const res = await alertApi.getAlertRuleTemplates()
        if (res.code === 200) {
          this.ruleTemplates = res.data || []
        } else {
          this.$message.error(res.msg || '获取模板列表失败')
        }
      } catch (error) {
        console.error('获取告警规则模板出错：', error)
        this.$message.error('获取模板列表失败')
      } finally {
        this.templateLoading = false
      }
    },

    // 应用模板创建规则
    handleSelectTemplate(template) {
      this.dialogTitle = '从模板创建告警规则'
      this.editingRuleId = null
      this.resetForm()
      this.ruleForm.bridgeId = this.selectedBridge
      // 将模板数据填充到表单
      this.ruleForm.ruleName = template.ruleName ? template.ruleName + '_副本' : ''
      this.ruleForm.detectionMethod = template.detectionMethod || ''
      this.ruleForm.alertLevel = template.alertLevel || ''
      this.ruleForm.alertMessage = template.alertMessage || ''
      if (template.alertMethods) {
        this.ruleForm.alertMethods = template.alertMethods.split(',')
      }
      this.ruleForm.status = 1
      this.templateDialogVisible = false
      this.dialogVisible = true
    },

    // 删除规则
    handleDelete(row) {
      this.$confirm('此操作将永久删除该告警规则，是否继续？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          this.loading = true
          const res = await alertApi.deleteAlertRule(row.id)
          if (res.code === 200) {
            this.$message.success('删除成功')
            this.fetchAlertRules()
          } else {
            this.$message.error(res.msg || '删除失败')
          }
        } catch (error) {
          console.error('删除告警规则出错：', error)
          this.$message.error('删除失败')
        } finally {
          this.loading = false
        }
      }).catch(() => {
        // 取消删除
      })
    },
    
    // 修改规则状态
    async handleStatusChange(row) {
      try {
        this.loading = true
        const res = await alertApi.updateAlertRuleStatus(row.id, row.status)
        if (res.code === 200) {
          this.$message.success(`已${row.status === 1 ? '启用' : '禁用'}`)
        } else {
          // 还原状态
          row.status = row.status === 1 ? 0 : 1
          this.$message.error(res.msg || '状态修改失败')
        }
      } catch (error) {
        // 还原状态
        row.status = row.status === 1 ? 0 : 1
        console.error('修改规则状态出错：', error)
        this.$message.error('状态修改失败')
      } finally {
        this.loading = false
      }
    },
    
    // 重置表单
    resetForm() {
      if (this.$refs.ruleForm) {
        this.$refs.ruleForm.resetFields()
      }
      this.ruleForm = {
        ruleName: '',
        areaId: '',
        detectionMethod: '',
        alertLevel: '',
        alertMessage: '',
        alertMethods: [],
        status: 1,
        bridgeId: this.selectedBridge
      }
      this.conditionForm = {
        speedThreshold: 8,
        heightThreshold: 15,
        durationMinutes: 30,
        rawJson: ''
      }
      this.currentAreaInfo = null
    },
    
    // 解析规则条件 JSON 到条件表单
    parseRuleCondition(ruleCondition) {
      const defaultForm = {
        speedThreshold: 8,
        heightThreshold: 15,
        durationMinutes: 30,
        rawJson: ''
      }
      this.conditionForm = { ...defaultForm }
      if (!ruleCondition) return
      try {
        const condition = typeof ruleCondition === 'string' ? JSON.parse(ruleCondition) : ruleCondition
        if (condition && typeof condition === 'object') {
          this.conditionForm.speedThreshold = condition.speedThreshold ?? 8
          this.conditionForm.heightThreshold = condition.heightThreshold ?? 15
          this.conditionForm.durationMinutes = condition.durationMinutes ?? 30
          this.conditionForm.rawJson = JSON.stringify(condition, null, 2)
        }
      } catch (e) {
        // 非 JSON 时保留原文本
        this.conditionForm.rawJson = String(ruleCondition)
      }
    },
    
    // 插入变量到告警信息
    insertVariable(variable) {
      if (!this.ruleForm.alertMessage) this.ruleForm.alertMessage = ''
      this.ruleForm.alertMessage += variable
    },
    
    // 分页处理
    handleSizeChange(val) {
      this.pagination.pageSize = val
      this.fetchAlertRules()
    },
    
    handleCurrentChange(val) {
      this.pagination.pageNum = val
      this.fetchAlertRules()
    }
  },
  computed: {
    // 不进行过滤
    filteredAlertTypes() {
      return this.alertTypes;
    },
    // 会遇规则只能绑定“桥区水域”区域；其它类型显示全部区域
    formAreaOptions() {
      if (this.ruleForm.detectionMethod === 'MEETING') {
        return this.areas.filter(a => this.isBridgeWaterArea(a))
      }
      return this.areas
    },
    filteredAlertTypes_V1() {
      if (!this.currentAreaInfo) return this.alertTypes;
      
      // 区域名称判断，包含"线"的被判定为线段，否则为区域
      const isLineSegment = this.currentAreaInfo.areaName.includes('线');
      
      // 区域类型判断
      const isNavigationArea = this.currentAreaInfo.areaName.includes('通航区');
      const isBridgePier = this.currentAreaInfo.areaName.includes('桥墩');
      const isAlertArea = this.currentAreaInfo.areaName.includes('警戒区');
      
      // 根据类型返回允许的告警动作
      let allowedActions = [];
      
      if (isLineSegment) {
        // 线段类型区域 - 桥梁线允许"超高"，普通线允许"穿越"
        allowedActions = this.currentAreaInfo.areaName.includes('桥梁') ? ['HIGH'] : ['CROSS'];
      } else if (isNavigationArea) {
        // 通航区允许"偏航"、"滞留"和"进入/离开"
        allowedActions = ['YAW','STAY', 'ENTRY', 'EXIT'];
      } else if (isBridgePier) {
        // 桥墩区域允许"碰撞"
        allowedActions = ['COLLISION'];
      } else {
        // 普通区域允许"进入"、"离开"、"滞留"、"超速"
        allowedActions = ['ENTRY', 'EXIT', 'STAY', 'SPEED'];
        
        // 如果是特殊的黑名单区域
        if (this.currentAreaInfo.areaName.includes('黑名单')) {
          allowedActions.push('BLACKLIST');
        }
      }
      
      // 根据允许的动作筛选告警类型
      return this.alertTypes.filter(type => allowedActions.includes(type.value));
    }
  }
}
</script>

<style scoped>
.alert-settings-container {
  padding: 20px;
}

.header-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-section h2 {
  margin: 0;
  font-size: 20px;
  font-weight: 500;
}

.header-actions {
  display: flex;
  align-items: center;
}

.toolbar {
  margin-bottom: 20px;
}

.empty-tip {
  text-align: center;
  color: #909399;
  padding: 50px 0;
  font-size: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.empty-tip i {
  font-size: 40px;
  margin-bottom: 15px;
  color: #E6A23C;
}

.pagination {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

.form-help {
  margin-top: 8px;
  font-size: 12px;
  color: #909399;
}

.variable-tag {
  margin-right: 5px;
  margin-bottom: 5px;
  cursor: pointer;
}

.variable-tag:hover {
  opacity: 0.8;
}

.alert-methods {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.alert-method-tag {
  margin-right: 3px;
  margin-bottom: 3px;
}

.condition-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.condition-label {
  font-size: 14px;
  color: #606266;
  white-space: nowrap;
}

.condition-help {
  font-size: 12px;
  color: #909399;
}
</style>